import React, { useState, useMemo } from 'react';
import { KatButton, KatModal } from '@amzn/katal-react';
import { BatcherService, FunctionMetadata } from '../../services/Batcher/BatcherService';
import './BatcherPage.scss';

interface BatcherPageProps {
    stage: string;
    realm: string;
}

const BatcherPage: React.FC<BatcherPageProps> = ({ stage, realm }) => {
    // Functions the user has added to the chain
    const [chain, setChain] = useState<FunctionMetadata[]>([]);

    // Modal + picker state
    const [modalOpen, setModalOpen] = useState(false);
    const [availableFunctions, setAvailableFunctions] = useState<FunctionMetadata[]>([]);
    // Functions are fetched once (on first open) and cached for subsequent opens.
    const [functionsLoaded, setFunctionsLoaded] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const PAGE_SIZE = 10;

    const filteredFunctions = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return availableFunctions;
        return availableFunctions.filter(fn => fn.functionName.toLowerCase().includes(term));
    }, [availableFunctions, search]);

    const totalPages = Math.max(1, Math.ceil(filteredFunctions.length / PAGE_SIZE));
    // Clamp the page in case filtering shrank the list below the current page.
    const currentPage = Math.min(page, totalPages - 1);
    const pageFunctions = filteredFunctions.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

    const handleOpenModal = async () => {
        setSearch('');
        setPage(0);

        // Use the cached list if we've already fetched successfully.
        if (functionsLoaded) {
            setModalOpen(true);
            return;
        }

        setError('');
        setLoading(true);
        try {
            const functions = await BatcherService.listFunctions(stage, realm);
            setAvailableFunctions(Array.isArray(functions) ? functions : []);
            setFunctionsLoaded(true);
            setModalOpen(true);
        } catch (e: any) {
            setError(e.message || 'Failed to load functions');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(0);
    };

    const handleAddFunction = (fn: FunctionMetadata) => {
        setChain(prev => [...prev, fn]);
        setModalOpen(false);
    };

    const handleRemoveFunction = (index: number) => {
        setChain(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="batcher-page">
            <div className="batcher-header">
                <h1 className="batcher-title">Select Functions</h1>
                <p className="batcher-subtitle">Select functions and run them as a chain.</p>
            </div>

            <div className="batcher-panel">
                <div className="batcher-panel-header">
                    <span className="batcher-panel-title">Functions</span>
                    <KatButton
                        variant="secondary"
                        onClick={handleOpenModal}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : '+ Add a function to chain'}
                    </KatButton>
                </div>

                {error && <div className="batcher-error">{error}</div>}

                {chain.length === 0 ? (
                    <div className="batcher-empty">No functions added</div>
                ) : (
                    <ol className="batcher-chain">
                        {chain.map((fn, index) => (
                            <li key={`${fn.functionIdentifier}-${index}`} className="batcher-chain-item">
                                <span className="batcher-chain-item-name">{fn.functionName}</span>
                                <KatButton
                                    variant="link"
                                    onClick={() => handleRemoveFunction(index)}
                                >
                                    Remove
                                </KatButton>
                            </li>
                        ))}
                    </ol>
                )}
            </div>

            {modalOpen && (
            <KatModal
                visible={modalOpen}
                title="Add functions to chain"
                onClose={() => setModalOpen(false)}
            >
                <div className="batcher-modal-body">
                    <input
                        type="text"
                        className="batcher-search"
                        placeholder="Search functions by name..."
                        value={search}
                        onChange={e => handleSearchChange(e.target.value)}
                    />

                    <div className="batcher-function-list">
                        {filteredFunctions.length === 0 ? (
                            <div className="batcher-picker-empty">
                                {availableFunctions.length === 0 ? 'No functions available' : 'No functions match your search'}
                            </div>
                        ) : (
                            pageFunctions.map(fn => (
                                <button
                                    key={fn.functionIdentifier}
                                    type="button"
                                    className="batcher-function-item"
                                    onClick={() => handleAddFunction(fn)}
                                >
                                    <span className="batcher-function-item-name">{fn.functionName}</span>
                                </button>
                            ))
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="batcher-pagination">
                            <KatButton
                                variant="link"
                                disabled={currentPage === 0}
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                            >
                                Previous
                            </KatButton>
                            <span className="batcher-pagination-info">
                                Page {currentPage + 1} of {totalPages}
                            </span>
                            <KatButton
                                variant="link"
                                disabled={currentPage >= totalPages - 1}
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            >
                                Next
                            </KatButton>
                        </div>
                    )}
                </div>

                <div slot="footer" className="batcher-modal-footer">
                    <KatButton variant="link" onClick={() => setModalOpen(false)}>Cancel</KatButton>
                </div>
            </KatModal>
            )}
        </div>
    );
};

export default BatcherPage;
